import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import {
  updateWorksheets,
  UpdateWorksheetsResponse,
  UpdateWorksheetStepGroup,
} from 'notebook/worksheets/network/WorksheetDAO';
import {Worksheet, WorksheetStepDocumentSelection} from 'notebook/worksheets/WorksheetConstants';
import {useValidatedWorksheetContext} from 'notebook/worksheets/WorksheetContext';
import WorksheetStepSelector from 'notebook/worksheets/workspace/WorksheetStepSelector';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeWorksheetAddOrReorderStepEventMetadata,
  WORKSHEET_ADD_STEP_STARTED_EVENT,
  WORKSHEET_ADD_STEP_ENDED_EVENT,
  WORKSHEET_REORDER_STEP_STARTED_EVENT,
  WORKSHEET_REORDER_STEP_ENDED_EVENT,
} from 'performance/processWorksheetsEvents';
import AddStepModal, {StepPlacement} from 'procedures/worksheets/AddStepModal';
import {usePMEIsAndMethodTasks} from 'procedures/worksheets/ProcedureWorksheetContext';
import {getProcedureType} from 'procedures/worksheets/WorksheetUtils';
import RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY} from 'ui-library/Colors';
import legacyRunModal from 'ui-library/legacyRunModal';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import {withLoadingScreen} from 'util/Promises';

interface ExecutionStepSelectorSidebarProps {
  setSelectedStep: (stepGroupIndex?: number, stepIndex?: number) => void;
  onAddStepCompleted: (stepGroupIndex?: number, stepIndex?: number) => void;
  isWorksheetLocked: boolean;
  selectedStep: WorksheetStepDocumentSelection;
  isWorksheetInReview?: boolean;
}

export default function ExecutionStepSelectorSidebar(props: ExecutionStepSelectorSidebarProps): JSX.Element {
  const {worksheet, refetch} = useValidatedWorksheetContext();

  return (
    <RealtimeUpdateSubscriptionWrapper apiId={worksheet.id} refetch={refetch} messageFilter="no_filter">
      <div {...getTestProps({component: 'ExecutionStepSelectorSidebar', element: 'root'})}>
        <PMEIConnectedExecutionStepSelectorSidebar {...props} />
      </div>
    </RealtimeUpdateSubscriptionWrapper>
  );
}

function _mapStepGroups(stepGroups: Worksheet['worksheetStepGroups']): Array<UpdateWorksheetStepGroup> {
  return stepGroups.map(({worksheetSteps}) => ({
    steps: worksheetSteps.map(({document}) => ({
      documentId: document.id,
    })),
  }));
}

export function _makeOverflowMenuOptions(
  worksheetStepGroups: Worksheet['worksheetStepGroups'],
  stepGroupIndex: number,
  stepIndex: number,
  isStructuralEditingEnabled: boolean,
  reorderStep: (stepGroupIndex: number, stepIndex: number, desiredStepIndex: number) => Promise<void>
): Array<OverflowMenuOption> {
  const numberOfStepsInGroup = worksheetStepGroups[stepGroupIndex].worksheetSteps.length;
  return [
    {
      contents: <>Move up</>,
      onSelect: () => {
        reorderStep(stepGroupIndex, stepIndex, stepIndex - 1);
      },
      isDisabled: stepIndex === 0 || !isStructuralEditingEnabled,
      icon: 'arrow-up',
    },
    {
      contents: <>Move down</>,
      contentClassName: css(styles.overflowMenuDivider),
      onSelect: () => {
        reorderStep(stepGroupIndex, stepIndex, stepIndex + 1);
      },
      isDisabled: stepIndex === numberOfStepsInGroup - 1 || !isStructuralEditingEnabled,
      icon: 'arrow-down',
    },
    {
      contents: <>Move to the top of group</>,
      onSelect: () => {
        reorderStep(stepGroupIndex, stepIndex, 0);
      },
      isDisabled: stepIndex === 0 || !isStructuralEditingEnabled,
      icon: 'arrow-up-to-line',
    },
    {
      contents: <>Move to the bottom of group</>,
      onSelect: () => {
        reorderStep(stepGroupIndex, stepIndex, numberOfStepsInGroup - 1);
      },
      isDisabled: stepIndex === numberOfStepsInGroup - 1 || !isStructuralEditingEnabled,
      icon: 'arrow-down-to-line',
    },
  ];
}

interface PMEIConnectedExecutionStepSelectorSidebarProps extends ExecutionStepSelectorSidebarProps {}

function collectAllStepNames(worksheetStepGroups: Worksheet['worksheetStepGroups']): Array<string> {
  return worksheetStepGroups.flatMap((group) => group.worksheetSteps.map((step) => step.document.name));
}

function PMEIConnectedExecutionStepSelectorSidebar(
  props: PMEIConnectedExecutionStepSelectorSidebarProps
): JSX.Element {
  const {setSelectedStep, onAddStepCompleted} = props;
  const {worksheet, refetch, isExecutionEnabled} = useValidatedWorksheetContext();
  const pmeisAndTasks = usePMEIsAndMethodTasks();
  const methodTasks = pmeisAndTasks.map((value) => value.task).filter((task) => task != null);
  const pmeis = pmeisAndTasks.map((value) => value.pmei);
  const procedureType = getProcedureType(pmeis);
  const worksheetStepGroups = worksheet.worksheetStepGroups;
  // An archived worksheet is read-only, so structural edits (adding or reordering steps) must be blocked.
  const isWorksheetArchived = worksheet.archiveData.archived;
  const isStructuralEditingEnabled = isExecutionEnabled && !isWorksheetArchived;

  const updateWorksheet = useCallback(
    (stepGroups: Array<UpdateWorksheetStepGroup>): Promise<UpdateWorksheetsResponse> =>
      updateWorksheets([
        {
          id: worksheet.id,
          stepGroups,
        },
      ]),
    [worksheet]
  );

  const onUpdateCompletion = useCallback(
    async (mappedStepGroups: Array<UpdateWorksheetStepGroup>) => {
      await updateWorksheet(mappedStepGroups);
      await refetch();
    },
    [refetch, updateWorksheet]
  );

  const addStep = useCallback(
    async (
      stepGroupIndex: number,
      stepPlacement: StepPlacement,
      stepName: string,
      fromId?: string
    ): Promise<void> => {
      if (!isStructuralEditingEnabled) {
        return;
      }
      logProfilerTimingEvent(WORKSHEET_ADD_STEP_STARTED_EVENT);
      const mappedStepGroups = _mapStepGroups(worksheet.worksheetStepGroups);
      const steps = mappedStepGroups[stepGroupIndex].steps;
      const stepIndex = stepPlacement === StepPlacement.BOTTOM ? steps.length : 0;
      steps.splice(stepIndex, 0, {name: stepName, fromId});

      await withLoadingScreen(
        (async () => {
          await onUpdateCompletion(mappedStepGroups);
          onAddStepCompleted(stepGroupIndex, stepIndex);
        })()
      );
      logProfilerTimingEvent(
        WORKSHEET_ADD_STEP_ENDED_EVENT,
        composeWorksheetAddOrReorderStepEventMetadata(methodTasks, procedureType)
      );
      notifications.success(`${stepName} has been added`);
    },
    [
      isStructuralEditingEnabled,
      worksheet,
      onUpdateCompletion,
      onAddStepCompleted,
      methodTasks,
      procedureType,
    ]
  );

  const reorderStep = useCallback(
    async (stepGroupIndex: number, stepIndex: number, desiredStepIndex: number): Promise<void> => {
      if (!isStructuralEditingEnabled) {
        return;
      }
      logProfilerTimingEvent(WORKSHEET_REORDER_STEP_STARTED_EVENT);
      const mappedStepGroups = _mapStepGroups(worksheet.worksheetStepGroups);
      const step = mappedStepGroups[stepGroupIndex].steps[stepIndex];
      mappedStepGroups[stepGroupIndex].steps.splice(stepIndex, 1);
      mappedStepGroups[stepGroupIndex].steps.splice(desiredStepIndex, 0, step);
      await withLoadingScreen(
        (async () => {
          await onUpdateCompletion(mappedStepGroups);
          setSelectedStep(stepGroupIndex, desiredStepIndex);
        })()
      );
      logProfilerTimingEvent(
        WORKSHEET_REORDER_STEP_ENDED_EVENT,
        composeWorksheetAddOrReorderStepEventMetadata(methodTasks, procedureType)
      );
    },
    [isStructuralEditingEnabled, worksheet, onUpdateCompletion, setSelectedStep, methodTasks, procedureType]
  );

  const makeOverflowMenu = useCallback(
    (stepGroupIndex: number, stepIndex: number): Array<OverflowMenuOption> =>
      _makeOverflowMenuOptions(
        worksheetStepGroups,
        stepGroupIndex,
        stepIndex,
        isStructuralEditingEnabled,
        reorderStep
      ),
    [reorderStep, worksheetStepGroups, isStructuralEditingEnabled]
  );

  return (
    <WorksheetStepSelector
      {...props}
      isAddStepDisabled={!isStructuralEditingEnabled || props.isWorksheetInReview}
      onAddStepClicked={(e, stepGroupIndex, stepGroupName) => {
        e.stopPropagation();
        const existingStepNames = collectAllStepNames(worksheetStepGroups);
        legacyRunModal(AddStepModal, {
          onRequestSubmit: ({stepPlacement, stepName, fromId}) => {
            addStep(stepGroupIndex, stepPlacement, stepName, fromId);
          },
          existingStepNames,
          stepGroupName,
        });
      }}
      makeOverflowMenuItems={makeOverflowMenu}
      isWorksheetInReview={props.isWorksheetInReview}
    />
  );
}

const styles = StyleSheet.create({
  overflowMenuDivider: {
    borderBottom: `1px solid ${GRAY}`,
  },
  icon: {
    paddingLeft: '12px',
  },
});
